import { Vector3 } from 'three';

/**
 * Central tuning constants. Keeping every magic number here (instead of
 * scattered across the codebase) makes the game easy to balance and reason
 * about — a single source of truth for designers.
 */

export const CITY = {
  BLOCK_SIZE: 60, // footprint of a city block (metres)
  ROAD_WIDTH: 22, // gap between blocks
  CURB_HEIGHT: 0.18, // realistic low sidewalk
  GRID: 11, // GRID x GRID blocks
} as const;

/** Distance between consecutive block centres. */
export const BLOCK_STEP = CITY.BLOCK_SIZE + CITY.ROAD_WIDTH;
/** Half the city width — the world spans [-HALF_CITY, +HALF_CITY]. */
export const HALF_CITY = (CITY.GRID * BLOCK_STEP) / 2;

export const PLAYER = {
  EYE_HEIGHT: 1.7,
  RADIUS: 0.5,
  HEIGHT: 1.8,
  SPAWN: new Vector3(0, 80, HALF_CITY - 30), // start high, fall into a swing
  LOOK_SENSITIVITY: 0.0022,
} as const;

export const PHYSICS = {
  GRAVITY: 34,
  RUN_SPEED: 13,
  RUN_ACCEL: 12, // how snappily ground speed approaches target
  AIR_ACCEL: 26,
  JUMP_VELOCITY: 14,
  CLIMB_SPEED: 7.5,
  WALL_STICK: 1.8, // inward velocity that keeps you pinned to a wall
  WALL_LEAP_OUT: 9,
  WALL_LEAP_UP: 11,
  CLIMB_RELOCK_DELAY: 0.4, // seconds you can't re-cling after leaping off
  MANTLE_REACH: 1.0, // how close to the roof before you vault over
} as const;

export const WEB = {
  MAX_LENGTH: 240,
  MIN_LENGTH: 7,
  REEL_SPEED: 22, // metres/sec the web shortens while reeling in
  ATTACH_SLACK: 0.96, // taut-ness of a freshly fired web
  CONSTRAINT_ITERATIONS: 2,
} as const;

export const CAMERA = {
  BASE_FOV: 76,
  MAX_FOV_BOOST: 15, // extra degrees at high speed
  FOV_SPEED_FACTOR: 0.32,
  FOV_LERP: 4,
  BOB_AMPLITUDE: 0.06,
  NEAR: 0.1,
  FAR: 2200,
} as const;

export const POPULATION = {
  CARS: 95,
  PEDESTRIANS: 170,
  BIRD_FLOCKS: 7,
  BALLOON_CLUSTERS: 12,
  BILLBOARDS: 34,
} as const;

export const PALETTE = {
  buildings: [
    0xff8a5c, 0x6cc4ff, 0xffd23f, 0x9ad36b, 0xc792ea, 0xff6f91, 0x5ec9b1, 0xf2a65a,
    0x7aa0ff, 0xe8e2d0, 0xff5d5d, 0x88d8e0,
  ],
  cars: [0xff4d4d, 0x3a78ff, 0xffd23f, 0x39d98b, 0xffffff, 0x1b1b1b, 0xff8a3d, 0x9b5cff],
  shirts: [0xff5d5d, 0x4d8bff, 0x39d98b, 0xffd23f, 0xff8a3d, 0xc792ea, 0xffffff, 0x20c4c4],
  skin: [0xffd9b3, 0xf1c27d, 0xc68642, 0x8d5524],
  balloons: [0xff5d5d, 0x4d8bff, 0x39d98b, 0xffd23f, 0xff8a3d, 0xc792ea],
  neon: [0xff3b6b, 0x39e6ff, 0xffe23b, 0x8a5cff, 0x3bff8f, 0xff8a3d],
} as const;
